import { Canvas } from '@ritabygglov/core';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import { canvasService } from '../../apiServices/CanvasService';
import { useAppState } from '../../context/AppState/useAppState';
import { getClientCoordinates } from '../../context/MouseEvents/helpers';
import { useToast } from '../../context/Toast/useToast';
import { useUser } from '../../context/User/useUser';
import { useRoomId } from '../../hooks/useRoomId';
import { ARROW_HALF_SIZE, getArrowCenter } from './arrowGeometry';

export const NorthArrow: React.FC = () => {
  const { viewBox } = useAppState();
  const { fetchUser, ...user } = useUser();
  const canvasId = useRoomId();
  const canvas = user.canvases.find((c) => c.canvasId === canvasId);
  const toast = useToast();
  const intl = useIntl();
  const location = useLocation();
  const isExport = location.pathname.includes('export');

  const [isHovered, setIsHovered] = useState(false);
  const [showNorthArrow, setShowNorthArrow] = useState(
    canvas?.showNorthArrow ?? true
  );
  const [arrowRotation, setArrowRotation] = useState(
    canvas?.northArrowRotation ?? 0
  );
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (canvas) {
      setShowNorthArrow(canvas.showNorthArrow);
      setArrowRotation(canvas.northArrowRotation);
    }
  }, [canvas, canvas?.showNorthArrow, canvas?.northArrowRotation]);

  const updateCanvas = useCallback(
    (form: Partial<Canvas>) => {
      canvasService
        .editCanvas(canvasId, form)
        .then(() => {
          fetchUser();
        })
        .catch((e: Error) => {
          toast.addToast({
            message: e.message,
            type: 'error',
          });
        });
    },
    [canvasId, fetchUser, toast]
  );

  const debouncedUpdate = useMemo(
    () => debounce(updateCanvas, 400),
    [updateCanvas]
  );

  const onClickNorthArrow = () => {
    const newShowNorthArrow = !showNorthArrow;
    setShowNorthArrow(newShowNorthArrow);
    debouncedUpdate({
      northArrowRotation: arrowRotation,
      showNorthArrow: newShowNorthArrow,
    });
    if (!newShowNorthArrow) {
      toast.addToast({
        message: intl.formatMessage({ id: 'northArrow.hiddenToast' }),
        type: 'success',
      });
    }
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  useEffect(() => {
    if (!canvas) return;

    const [midX, midY] = getArrowCenter(canvas.pageFormat);

    const onMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const clientCoordinates = getClientCoordinates(e, viewBox);
        const deltaX = clientCoordinates.x - midX;
        const deltaY = clientCoordinates.y - midY;
        const rotate = Math.round(
          (Math.atan2(deltaY, deltaX) * 180) / Math.PI + 90
        );
        setArrowRotation(rotate);
        debouncedUpdate({
          northArrowRotation: rotate,
          showNorthArrow,
        });
      }
    };

    const onMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [canvas, debouncedUpdate, isDragging, showNorthArrow, viewBox]);

  if (!canvas) return null;

  if (isExport && !showNorthArrow) {
    return null;
  }

  const [centerX, centerY] = getArrowCenter(canvas.pageFormat);
  const arrowSize = ARROW_HALF_SIZE * 2;
  const halfSize = ARROW_HALF_SIZE;

  return canvas.showNorthArrow ? (
    <g
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={handleMouseDown}
    >
      <rect
        x={centerX - halfSize - 100}
        y={centerY - halfSize}
        width={100 + arrowSize}
        height={arrowSize}
        fill="transparent"
        pointerEvents="all"
      />
      {isHovered && !isExport && (
        <text
          x={centerX - halfSize - 10}
          y={centerY + 5}
          fill="#046df3"
          fontSize="14"
          fontWeight="500"
          textAnchor="end"
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={onClickNorthArrow}
        >
          {intl.formatMessage({ id: 'northArrow.hideButton' })}
        </text>
      )}
      <g transform={`rotate(${arrowRotation}, ${centerX}, ${centerY})`}>
        {isHovered && (
          <>
            <circle
              cx={centerX}
              cy={centerY}
              r="32"
              fill="rgba(4, 109, 243, 0.1)"
              stroke="#046DF3"
            />
            <rect
              x={centerX - 8}
              y={centerY - halfSize}
              width="16"
              height="6"
              rx="4"
              fill="rgba(4, 109, 243, 1)"
              stroke="#046DF3"
            />
          </>
        )}
        <g
          transform={`translate(${centerX - halfSize + 26}, ${centerY - halfSize + 16})`}
        >
          <path
            opacity="0.8"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.99998 0L0 24C0.626353 23.2775 1.22794 22.6192 1.80877 22.0246C7.3217 16.3808 10.9637 16.4757 16.152 21.9202C16.746 22.5436 17.3603 23.237 18 24L8.99998 0ZM8.99998 2.84801L2.61739 19.8683C3.20595 19.348 3.78119 18.8926 4.34723 18.5037C5.95287 17.4004 7.53625 16.7971 9.17445 16.8147C10.8128 16.8323 12.3665 17.4693 13.9154 18.5819C14.3812 18.9164 14.8512 19.2973 15.328 19.7228L8.99998 2.84801Z"
            fill="black"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.00005 17.8153V0L18 24C14.5245 19.8544 11.7987 17.7616 9.00005 17.8153Z"
            fill="black"
          />
          <path
            d="M12.3169 23.2734V32.0007H11.2942L6.53853 25.1484H6.4533V32.0007H5.39648V23.2734H6.41921L11.1919 30.1428H11.2772V23.2734H12.3169Z"
            fill="black"
          />
        </g>
      </g>
    </g>
  ) : null;
};
